import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { cn } from "@/lib/util";
import { ArrowDownLeft, ArrowUpRight, Loader2, ArrowRightLeft, Globe, Network, Server } from "lucide-react";

const KIND_ICON = { domain: Globe, subdomain: Network, ip: Server };
const KIND_LABEL = { domain: "Dominio", subdomain: "Subdominio", ip: "IP" };

/**
 * Mini-log de reasignaciones — muestra los últimos activos que llegaron o
 * salieron del cliente actual mediante `POST /api/assets/reassign`.
 */
export function ClientReassignLogCard({ clientId }) {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/clients/${clientId}/reassignments?limit=10`)
      .then((r) => { if (alive) setEvents(r.data?.events || []); })
      .catch(() => { if (alive) setEvents([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [clientId]);

  return (
    <section className="border border-border bg-card/60 p-5" data-testid="reassign-log-card">
      <div className="flex items-center gap-2 mb-3">
        <ArrowRightLeft className="w-4 h-4 text-primary" />
        <div>
          <div className="overline">Auditoría de reasignaciones</div>
          <h2 className="font-display text-lg font-medium">Movimientos recientes</h2>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-muted-foreground text-xs">
          <Loader2 className="w-3 h-3 animate-spin" /> Cargando…
        </div>
      ) : events.length === 0 ? (
        <div className="py-8 text-center text-xs text-muted-foreground border border-dashed border-border">
          Sin reasignaciones registradas para este cliente.
        </div>
      ) : (
        <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="reassign-log-list">
          {events.map((e) => {
            const isIn = e.direction === "in";
            const KindIcon = KIND_ICON[e.kind] || Globe;
            return (
              <li
                key={e.id}
                data-testid={`reassign-log-${e.id}`}
                className={cn(
                  "border px-3 py-2 font-mono text-[11px] flex items-start justify-between gap-2",
                  isIn ? "border-sev-low/40 bg-sev-low/5" : "border-sev-medium/40 bg-sev-medium/5"
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest border",
                        isIn
                          ? "border-sev-low/50 text-sev-low bg-sev-low/10"
                          : "border-sev-medium/50 text-sev-medium bg-sev-medium/10"
                      )}
                    >
                      {isIn ? <ArrowDownLeft className="w-2.5 h-2.5" /> : <ArrowUpRight className="w-2.5 h-2.5" />}
                      {isIn ? "Llegó" : "Salió"}
                    </span>
                    <KindIcon className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[9px] uppercase tracking-widest text-muted-foreground">
                      {KIND_LABEL[e.kind] || e.kind}
                    </span>
                    <span className="text-foreground truncate">{e.value}</span>
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {isIn ? "Desde " : "Hacia "}
                    {e.counterparty_id ? (
                      <Link
                        to={`/clients/${e.counterparty_id}`}
                        className="text-primary hover:underline"
                        data-testid={`reassign-log-counterparty-${e.id}`}
                      >
                        {e.counterparty_name}
                      </Link>
                    ) : (
                      <span className="text-foreground">{e.counterparty_name}</span>
                    )}
                    {" · por "}
                    <span className="text-foreground">{e.user_email}</span>
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                  {e.at ? new Date(e.at).toLocaleString() : "—"}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
